import React from 'react'
import classNames from 'classnames'
import { Chip, ChipOption, ChipPopover } from '../../../components/recipes/workflow/Chip'
import { Combobox } from '../../../components/ui/combobox'
import {
    formatScheduleOccurrence,
    formatScheduleSummary,
    formatScheduleTimeZoneAbbreviation,
} from '../format'
import type { ScheduleDraft, SchedulePreview, ScheduleTimezones } from '../types'

export type ScheduleEditorType = 'once' | 'guided' | 'advanced'

export type GuidedWeekday =
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
    | 'sunday'

export interface GuidedScheduleDraft {
    timezone: string
    weekdays: GuidedWeekday[]
    /** One to four local HH:mm values. Guided schedules require one shared minute. */
    times: string[]
}

export interface ScheduleEditorProps {
    /** The API-shaped draft. This component deliberately does not own persistence. */
    draft: ScheduleDraft | null
    timezones?: ScheduleTimezones | null
    preview?: SchedulePreview | null
    previewError?: string | null
    /** True when the draft came from an already-persisted schedule. */
    persistedSchedule?: boolean
    /** Reports whether the visible editor state is a valid writable draft. */
    onValidityChange?: (valid: boolean) => void
    nearbySchedule?: boolean
    adType?: number
    onDraftChange: (draft: ScheduleDraft) => void
    /** The caller supplies the server preview; no browser cron parser is used. */
    onPreview?: (draft: ScheduleDraft) => void | Promise<void>
}

const DAYS: { value: GuidedWeekday; label: string; cron: string }[] = [
    { value: 'monday', label: 'Mon', cron: '1' },
    { value: 'tuesday', label: 'Tue', cron: '2' },
    { value: 'wednesday', label: 'Wed', cron: '3' },
    { value: 'thursday', label: 'Thu', cron: '4' },
    { value: 'friday', label: 'Fri', cron: '5' },
    { value: 'saturday', label: 'Sat', cron: '6' },
    { value: 'sunday', label: 'Sun', cron: '0' },
]

const SCHEDULE_TYPE_OPTIONS: { value: ScheduleEditorType; label: string }[] = [
    { value: 'once', label: 'Once' },
    { value: 'guided', label: 'Recurring' },
    { value: 'advanced', label: 'Advanced' },
]

const sameDaySet = (a: GuidedWeekday[], b: GuidedWeekday[]) =>
    a.length === b.length && a.every((day) => b.includes(day))

/** The closed chip's label — "Every day", "Weekdays", or a short list. */
const daysSummary = (days: GuidedWeekday[]): string => {
    if (days.length === 0) return 'No days'
    const values = DAYS.map((day) => day.value)
    if (sameDaySet(days, values)) return 'Every day'
    if (sameDaySet(days, values.slice(0, 5))) return 'Weekdays'
    if (sameDaySet(days, values.slice(5))) return 'Weekends'
    return DAYS.filter((day) => days.includes(day.value))
        .map((day) => day.label)
        .join(', ')
}

export const isNegativeScheduleAdType = (adType?: number): boolean =>
    adType === 6 || adType === 7 || adType === 8

export const scheduleTimezone = (
    draft: ScheduleDraft | null,
    timezones?: ScheduleTimezones | null,
): string => {
    const available = timezones?.timezones ?? []
    const candidate = draft?.timezone || timezones?.default || 'UTC'
    // The endpoint already validates the profile default. Keep this guard in the
    // view because an editor can render before the timezone request completes.
    return available.length === 0 || available.includes(candidate) ? candidate : 'UTC'
}

const validTime = (time: string) => {
    const [hour, minute, ...rest] = time.split(':')
    return (
        rest.length === 0 &&
        hour?.length === 2 &&
        minute?.length === 2 &&
        Number.isInteger(Number(hour)) &&
        Number.isInteger(Number(minute)) &&
        Number(hour) >= 0 &&
        Number(hour) <= 23 &&
        Number(minute) >= 0 &&
        Number(minute) <= 59
    )
}

/**
 * Compile the guided UI without trying to interpret arbitrary cron syntax.
 * A single five-field expression cannot exactly pair different minutes with
 * different hours, so guided entries intentionally share a minute.
 */
export const compileGuidedSchedule = (
    guided: GuidedScheduleDraft,
): { draft: ScheduleDraft | null; error: string | null } => {
    const times: string[] = []
    for (const time of guided.times) {
        if (validTime(time) && !times.includes(time)) times.push(time)
    }
    if (times.length === 0) return { draft: null, error: 'Choose at least one time.' }
    if (times.length > 4) return { draft: null, error: 'Choose no more than 4 times.' }
    if (guided.weekdays.length === 0) return { draft: null, error: 'Choose at least one day.' }

    const minutes = new Set(times.map((time) => time.slice(3)))
    if (minutes.size !== 1) {
        return {
            draft: null,
            error: 'Guided times must use the same minute (for example, 06:30 and 10:30).',
        }
    }

    const hours: number[] = []
    for (const time of times) {
        const hour = Number(time.slice(0, 2))
        if (!hours.includes(hour)) hours.push(hour)
    }
    hours.sort((a, b) => a - b)
    const selectedWeekdays = DAYS.filter((day) => guided.weekdays.includes(day.value))
    const weekdays =
        selectedWeekdays.length === DAYS.length
            ? '*'
            : selectedWeekdays.length === 5 &&
                selectedWeekdays.every((day, index) => day === DAYS[index])
              ? '1-5'
              : selectedWeekdays.map((day) => day.cron).join(',')
    return {
        draft: {
            kind: 'cron',
            timezone: guided.timezone,
            cron_expression: `${Number(times[0].slice(3))} ${hours.join(',')} * * ${weekdays}`,
        },
        error: null,
    }
}

/** Returns a guided draft only when the existing cron can be represented exactly. */
export const parseGuidedScheduleDraft = (
    draft: ScheduleDraft | null,
): GuidedScheduleDraft | null => {
    if (draft?.kind !== 'cron') return null
    const fields = draft.cron_expression.trim().split(/\s+/)
    if (fields.length !== 5 || fields[2] !== '*' || fields[3] !== '*') return null
    const [minute, hour, , , weekday] = fields
    if (
        !/^\d{1,2}$/.test(minute) ||
        Number(minute) > 59 ||
        !/^\d{1,2}(,\d{1,2}){0,3}$/.test(hour)
    ) {
        return null
    }

    const hours = hour.split(',').map(Number)
    if (hours.some((value) => value > 23)) return null
    const weekdayValues =
        weekday === '*'
            ? DAYS.map((day) => day.value)
            : weekday === '1-5'
              ? DAYS.slice(0, 5).map((day) => day.value)
              : (weekday
                    .split(',')
                    .map((value) => DAYS.find((day) => day.cron === value)?.value)
                    .filter(Boolean) as GuidedWeekday[])
    if (weekdayValues.length === 0) return null
    return {
        timezone: draft.timezone,
        weekdays: weekdayValues,
        times: hours.map((value) => `${String(value).padStart(2, '0')}:${minute.padStart(2, '0')}`),
    }
}

export const guidedScheduleFromDraft = (
    draft: ScheduleDraft | null,
    timezone: string,
): GuidedScheduleDraft => {
    const fallback: GuidedScheduleDraft = {
        timezone,
        weekdays: DAYS.map((day) => day.value),
        times: ['09:00'],
    }
    return parseGuidedScheduleDraft(draft) ?? fallback
}

export const defaultGuidedScheduleDraft = (timezone = 'UTC'): ScheduleDraft => {
    const result = compileGuidedSchedule({
        timezone,
        weekdays: DAYS.map((day) => day.value),
        times: ['09:00'],
    })
    // The literal is only a defensive fallback; the constant draft above is valid.
    return result.draft ?? { kind: 'cron', timezone, cron_expression: '0 9 * * 0,1,2,3,4,5,6' }
}

const localParts = (instant: string | Date, timezone: string) => {
    const date = typeof instant === 'string' ? new Date(instant) : instant
    if (Number.isNaN(date.getTime())) return null
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        }).formatToParts(date)
        const get = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
        return {
            date: `${get('year')}-${get('month')}-${get('day')}`,
            time: `${get('hour')}:${get('minute')}`,
        }
    } catch {
        return null
    }
}

const offsetAt = (date: Date, timezone: string): string => {
    try {
        const part = new Intl.DateTimeFormat('en-US', {
            timeZone: timezone,
            timeZoneName: 'longOffset',
        })
            .formatToParts(date)
            .find((item) => item.type === 'timeZoneName')?.value
        if (!part || part === 'GMT') return '+00:00'
        const value = part.replace('GMT', '')
        return /^[+-]\d{2}:\d{2}$/.test(value) ? value : '+00:00'
    } catch {
        return '+00:00'
    }
}

const offsetMinutesAt = (date: Date, timezone: string): number => {
    const value = offsetAt(date, timezone)
    const match = /^([+-])(\d{2}):(\d{2})$/.exec(value)
    if (!match) return 0
    const minutes = Number(match[2]) * 60 + Number(match[3])
    return match[1] === '-' ? -minutes : minutes
}

const wallTime = (date: string, time: string): number | null => {
    const value = new Date(`${date}T${time}:00Z`).getTime()
    return Number.isNaN(value) ? null : value
}

/**
 * Resolve an IANA local wall minute without relying on the browser's own time
 * zone. Exact fold matches select the earlier instant; a nonexistent spring
 * minute moves forward by the gap, matching recurrence scheduling semantics.
 */
const resolveLocalWallTime = (date: string, time: string, timezone: string) => {
    const requested = wallTime(date, time)
    if (requested === null) return { date, time, offset: '+00:00' }

    const offsets: number[] = []
    for (const hours of [-48, -24, -12, 0, 12, 24, 48]) {
        const offset = offsetMinutesAt(new Date(requested + hours * 60 * 60 * 1000), timezone)
        if (!offsets.includes(offset)) offsets.push(offset)
    }
    const candidates = offsets
        .map((offset) => {
            const instant = new Date(requested - offset * 60 * 1000)
            const parts = localParts(instant, timezone)
            if (!parts) return null
            return { instant, parts, wall: wallTime(parts.date, parts.time) }
        })
        .filter(Boolean) as {
        instant: Date
        parts: { date: string; time: string }
        wall: number | null
    }[]
    const exact = candidates
        .filter((candidate) => candidate.parts.date === date && candidate.parts.time === time)
        .sort((a, b) => a.instant.getTime() - b.instant.getTime())
    const shifted = candidates
        .filter((candidate) => candidate.wall !== null && candidate.wall >= requested)
        .sort(
            (a, b) =>
                (a.wall ?? Number.MAX_SAFE_INTEGER) - (b.wall ?? Number.MAX_SAFE_INTEGER) ||
                a.instant.getTime() - b.instant.getTime(),
        )
    const selected = exact[0] ?? shifted[0]
    if (!selected) return { date, time, offset: offsetAt(new Date(requested), timezone) }
    return {
        date: selected.parts.date,
        time: selected.parts.time,
        offset: offsetAt(selected.instant, timezone),
    }
}

/** A minute-precision, explicitly-offset value for the write/preview API. */
export const oneTimeDraft = (date: string, time: string, timezone: string): ScheduleDraft => {
    const resolved = resolveLocalWallTime(date, time, timezone)
    return {
        kind: 'once',
        timezone,
        run_once_at: `${resolved.date}T${resolved.time}:00${resolved.offset}`,
    }
}

const initialOneTime = (timezone: string): ScheduleDraft => {
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000)
    const parts = localParts(tomorrow.toISOString(), timezone)
    return oneTimeDraft(parts?.date ?? tomorrow.toISOString().slice(0, 10), '09:00', timezone)
}

const typeFromDraft = (draft: ScheduleDraft | null): ScheduleEditorType => {
    if (draft?.kind === 'once') return 'once'
    return draft?.kind === 'cron' && !parseGuidedScheduleDraft(draft) ? 'advanced' : 'guided'
}

const draftKey = (draft: ScheduleDraft | null) =>
    draft
        ? draft.kind === 'once'
            ? `once:${draft.timezone}:${draft.run_once_at}`
            : `cron:${draft.timezone}:${draft.cron_expression}`
        : 'empty'

export const ScheduleEditor = ({
    draft,
    timezones,
    preview,
    previewError,
    persistedSchedule = false,
    onValidityChange,
    nearbySchedule = false,
    adType,
    onDraftChange,
    onPreview,
}: ScheduleEditorProps) => {
    const timezone = scheduleTimezone(draft, timezones)
    const [type, setType] = React.useState<ScheduleEditorType>(() => typeFromDraft(draft))
    const [guided, setGuided] = React.useState<GuidedScheduleDraft>(() =>
        guidedScheduleFromDraft(draft, timezone),
    )
    // The API draft represents the schedule that will be saved, but switching
    // editor types is a local editing concern. Keep each form's last value so
    // changing type is non-destructive; guided and advanced deliberately share
    // their cron value when moving directly between those two views.
    const draftsByTypeRef = React.useRef<Partial<Record<ScheduleEditorType, ScheduleDraft>>>(
        draft ? { [typeFromDraft(draft)]: draft } : {},
    )
    // A brand-new draft can be seeded before the async profile timezone
    // response arrives. Track that seed so a later valid default may replace
    // it, while a persisted or explicitly selected UTC value remains intact.
    const autoSeededTimezoneRef = React.useRef(
        !persistedSchedule && timezones == null && draft?.timezone === 'UTC',
    )
    const externalDraftKey = draftKey(draft)
    const previewCallback = React.useRef(onPreview)
    const emittedDraftKey = React.useRef<string | null>(null)

    const emitDraft = React.useCallback(
        (next: ScheduleDraft) => {
            emittedDraftKey.current = draftKey(next)
            onDraftChange(next)
        },
        [onDraftChange],
    )

    React.useEffect(() => {
        previewCallback.current = onPreview
    }, [onPreview])

    React.useEffect(() => {
        // Do not turn an intentionally selected advanced expression back into
        // guided controls merely because it happens to use a guided-shaped cron.
        if (emittedDraftKey.current !== externalDraftKey) {
            setType(typeFromDraft(draft))
            setGuided(guidedScheduleFromDraft(draft, timezone))
        }
    }, [externalDraftKey, timezone])

    const disabled = isNegativeScheduleAdType(adType)
    const guidedResult = compileGuidedSchedule({ ...guided, timezone })
    const onceParts = draft?.kind === 'once' ? localParts(draft.run_once_at, draft.timezone) : null
    const onceDate = onceParts?.date ?? new Date().toISOString().slice(0, 10)
    const onceTime = onceParts?.time ?? '09:00'
    const currentDraft: ScheduleDraft =
        type === 'once'
            ? draft?.kind === 'once'
                ? { ...draft, timezone }
                : initialOneTime(timezone)
            : type === 'guided'
              ? (guidedResult.draft ?? { kind: 'cron', timezone, cron_expression: '' })
              : draft?.kind === 'cron'
                ? { ...draft, timezone }
                : { kind: 'cron', timezone, cron_expression: '' }

    const editorValid =
        !disabled &&
        (type !== 'guided' || Boolean(guidedResult.draft)) &&
        (currentDraft.kind !== 'cron' || Boolean(currentDraft.cron_expression.trim())) &&
        (currentDraft.kind !== 'once' || Boolean(currentDraft.run_once_at))

    React.useEffect(() => {
        onValidityChange?.(editorValid)
    }, [editorValid, onValidityChange])

    React.useEffect(() => {
        if (
            !autoSeededTimezoneRef.current ||
            !draft ||
            !timezones?.default_is_valid ||
            !timezones.default ||
            timezones.default === 'UTC'
        ) {
            return
        }
        autoSeededTimezoneRef.current = false
        if (draft.kind === 'once') {
            const parts = localParts(draft.run_once_at, draft.timezone)
            if (!parts) return
            emitDraft(oneTimeDraft(parts.date, parts.time, timezones.default))
        } else {
            emitDraft({ ...draft, timezone: timezones.default })
        }
    }, [draft, emitDraft, timezones?.default, timezones?.default_is_valid])

    const previewKey = draftKey(currentDraft)

    React.useEffect(() => {
        if (
            disabled ||
            !previewCallback.current ||
            (type === 'guided' && !guidedResult.draft) ||
            (currentDraft.kind === 'cron' && !currentDraft.cron_expression.trim()) ||
            (currentDraft.kind === 'once' && !currentDraft.run_once_at)
        ) {
            return
        }
        const timer = window.setTimeout(() => previewCallback.current?.(currentDraft), 350)
        return () => window.clearTimeout(timer)
    }, [disabled, previewKey, type, Boolean(guidedResult.draft)])

    const emitGuided = (next: GuidedScheduleDraft) => {
        setGuided(next)
        const result = compileGuidedSchedule({ ...next, timezone: next.timezone || timezone })
        if (result.draft) emitDraft(result.draft)
    }

    const selectType = (next: ScheduleEditorType) => {
        draftsByTypeRef.current[type] = currentDraft
        setType(next)
        if (next === 'once') {
            autoSeededTimezoneRef.current = draft == null && timezones == null
            const nextDraft = draftsByTypeRef.current.once ?? initialOneTime(timezone)
            draftsByTypeRef.current.once = nextDraft
            emitDraft(nextDraft)
        }
        if (next === 'guided') {
            const nextGuided = guidedScheduleFromDraft(
                type === 'advanced'
                    ? draftsByTypeRef.current.advanced
                    : (draftsByTypeRef.current.guided ?? draftsByTypeRef.current.advanced),
                timezone,
            )
            setGuided(nextGuided)
            const result = compileGuidedSchedule(nextGuided)
            if (result.draft) {
                draftsByTypeRef.current.guided = result.draft
                emitDraft(result.draft)
            }
        }
        if (next === 'advanced') {
            const nextDraft = (type === 'guided'
                ? draftsByTypeRef.current.guided
                : (draftsByTypeRef.current.advanced ?? draftsByTypeRef.current.guided)) ?? {
                kind: 'cron' as const,
                timezone,
                cron_expression: '',
            }
            draftsByTypeRef.current.advanced = nextDraft
            emitDraft(nextDraft)
        }
    }

    const changeTimezone = (resolved: string) => {
        autoSeededTimezoneRef.current = false
        if (type === 'guided') emitGuided({ ...guided, timezone: resolved })
        else if (type === 'once') emitDraft(oneTimeDraft(onceDate, onceTime, resolved))
        else emitDraft({ ...currentDraft, timezone: resolved } as ScheduleDraft)
    }

    // Building each label constructs an Intl.DateTimeFormat; with the real API's
    // ~400-zone list this is expensive enough to skip on every keystroke.
    const availableTimezones = timezones?.timezones
    const timezoneOptions = React.useMemo(
        () =>
            (availableTimezones ?? ['UTC']).map((zone) => ({
                value: zone,
                label: `${zone.replace(/_/g, ' ')} (${formatScheduleTimeZoneAbbreviation(zone)})`,
            })),
        [availableTimezones],
    )

    return (
        <section
            aria-label="Schedule editor"
            className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-[12px]"
        >
            {disabled ? (
                <p role="note" className="m-0 text-sm text-gray-600">
                    Scheduling is not available for negative keyword and negative target segments
                    yet.
                </p>
            ) : (
                <>
                    <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            Schedule type
                        </label>
                        <div role="group" aria-label="Schedule type">
                            <Chip
                                caret
                                content={
                                    <ChipPopover className="min-w-[180px]">
                                        {SCHEDULE_TYPE_OPTIONS.map((option) => (
                                            <ChipOption
                                                key={option.value}
                                                label={option.label}
                                                selected={type === option.value}
                                                onClick={() => selectType(option.value)}
                                            />
                                        ))}
                                    </ChipPopover>
                                }
                            >
                                {
                                    SCHEDULE_TYPE_OPTIONS.find((option) => option.value === type)
                                        ?.label
                                }
                            </Chip>
                        </div>
                    </div>

                    <div className="mt-3">
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            Time zone
                        </label>
                        <div role="group" aria-label="Time zone">
                            <Combobox
                                options={timezoneOptions}
                                value={timezone}
                                onChange={changeTimezone}
                                caretAlign="right"
                                className="w-full !px-2"
                            />
                        </div>
                    </div>
                    {timezones && !timezones.default_is_valid && (
                        <p role="note" className="mb-0 mt-1 text-xs text-amber-800">
                            The profile time zone is unavailable. UTC is selected.
                        </p>
                    )}

                    {type === 'once' && (
                        <div className="mt-3 grid grid-cols-2 gap-2">
                            <label className="text-sm text-gray-700">
                                Date
                                <input
                                    type="date"
                                    value={onceDate}
                                    onChange={(event) =>
                                        emitDraft(
                                            oneTimeDraft(event.target.value, onceTime, timezone),
                                        )
                                    }
                                    className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1.5"
                                />
                            </label>
                            <label className="text-sm text-gray-700">
                                Time
                                <input
                                    type="time"
                                    value={onceTime}
                                    step={60}
                                    onChange={(event) =>
                                        emitDraft(
                                            oneTimeDraft(onceDate, event.target.value, timezone),
                                        )
                                    }
                                    className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1.5"
                                />
                            </label>
                        </div>
                    )}

                    {type === 'guided' && (
                        <div className="mt-3">
                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Days
                            </label>
                            <div role="group" aria-label="Days">
                                <Chip
                                    caret
                                    className={classNames(
                                        guided.weekdays.length === 0 && 'ring-2 ring-red-300',
                                    )}
                                    content={
                                        <ChipPopover className="min-w-[160px]">
                                            {DAYS.map((day) => {
                                                const checked = guided.weekdays.includes(day.value)
                                                return (
                                                    <ChipOption
                                                        key={day.value}
                                                        label={day.label}
                                                        selected={checked}
                                                        dismiss={false}
                                                        onClick={() =>
                                                            emitGuided({
                                                                ...guided,
                                                                weekdays: checked
                                                                    ? guided.weekdays.filter(
                                                                          (value) =>
                                                                              value !== day.value,
                                                                      )
                                                                    : [
                                                                          ...guided.weekdays,
                                                                          day.value,
                                                                      ],
                                                            })
                                                        }
                                                    />
                                                )
                                            })}
                                        </ChipPopover>
                                    }
                                >
                                    {daysSummary(guided.weekdays)}
                                </Chip>
                            </div>
                            <div className="mt-3">
                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                    Local times
                                </label>
                                <div className="flex flex-wrap items-center gap-1.5">
                                    {guided.times.map((time, index) => (
                                        <div
                                            key={`${index}-${time}`}
                                            className="inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-white py-1 pl-2 pr-1"
                                        >
                                            <label
                                                className="sr-only"
                                                htmlFor={`schedule-time-${index}`}
                                            >
                                                Time {index + 1}
                                            </label>
                                            <input
                                                id={`schedule-time-${index}`}
                                                type="time"
                                                value={time}
                                                step={60}
                                                onChange={(event) =>
                                                    emitGuided({
                                                        ...guided,
                                                        times: guided.times.map((value, i) =>
                                                            i === index
                                                                ? event.target.value
                                                                : value,
                                                        ),
                                                    })
                                                }
                                                className="border-0 bg-transparent p-0 text-[13px] text-gray-800 focus:outline-none focus:ring-0"
                                            />
                                            {guided.times.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        emitGuided({
                                                            ...guided,
                                                            times: guided.times.filter(
                                                                (_, i) => i !== index,
                                                            ),
                                                        })
                                                    }
                                                    aria-label={`Remove time ${index + 1}`}
                                                    className="rounded p-0.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                                                >
                                                    <XIcon className="h-3 w-3" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {guided.times.length < 4 && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                emitGuided({
                                                    ...guided,
                                                    times: [...guided.times, '12:00'],
                                                })
                                            }
                                            className="inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-2 py-1 text-[13px] text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-50"
                                        >
                                            <PlusIcon className="h-3 w-3" />
                                            Add time
                                        </button>
                                    )}
                                </div>
                            </div>
                            {guidedResult.error && (
                                <p role="alert" className="mb-0 mt-2 text-sm text-red-700">
                                    {guidedResult.error}
                                </p>
                            )}
                        </div>
                    )}

                    {type === 'advanced' && (
                        <div className="mt-3">
                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                Cron expression
                                <input
                                    value={
                                        currentDraft.kind === 'cron'
                                            ? currentDraft.cron_expression
                                            : ''
                                    }
                                    onChange={(event) =>
                                        emitDraft({
                                            kind: 'cron',
                                            timezone,
                                            cron_expression: event.target.value,
                                        })
                                    }
                                    placeholder="minute hour day month weekday"
                                    aria-label="Cron expression"
                                    aria-describedby="schedule-cron-help schedule-cron-guidance"
                                    className="mt-1 block w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-[13px] font-normal normal-case tracking-normal text-gray-800 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/40"
                                />
                            </label>
                            <p id="schedule-cron-help" className="mb-0 mt-1 text-xs text-gray-500">
                                Standard five-field cron syntax. We validate it with the server
                                before saving.
                            </p>
                            <dl
                                id="schedule-cron-guidance"
                                aria-label="Schedule field guidance"
                                className="mb-0 mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-xs text-gray-500"
                            >
                                <dt className="font-medium text-gray-700">Minute</dt>
                                <dd className="m-0">0–59</dd>
                                <dt className="font-medium text-gray-700">Hour</dt>
                                <dd className="m-0">0–23</dd>
                                <dt className="font-medium text-gray-700">Day</dt>
                                <dd className="m-0">1–31</dd>
                                <dt className="font-medium text-gray-700">Month</dt>
                                <dd className="m-0">1–12 or JAN–DEC</dd>
                                <dt className="font-medium text-gray-700">Weekday</dt>
                                <dd className="m-0">0–7 or SUN–SAT (0 and 7 are Sunday)</dd>
                                <dt className="font-medium text-gray-700">Patterns</dt>
                                <dd className="m-0">* any, , list, - range, / step</dd>
                            </dl>
                        </div>
                    )}

                    <div className="mt-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                            Summary
                        </div>
                        <div className="mt-1.5 rounded p-[10px] text-[13px] text-gray-700">
                            <p className="m-0" aria-live="polite" aria-atomic="true">
                                {formatScheduleSummary(currentDraft, { showTimeZone: false })}
                            </p>
                        </div>
                    </div>
                    {nearbySchedule && (
                        <p role="alert" className="mb-0 mt-2 text-sm text-amber-800">
                            Another segment on this profile runs at about the same time. Segments
                            run one at a time, so this one may start late.
                        </p>
                    )}
                    {previewError && (
                        <p role="alert" className="mb-0 mt-2 text-sm text-red-700">
                            {previewError}
                        </p>
                    )}
                    {preview && (
                        <div className="mt-3">
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                {type === 'once' ? 'Next scheduled run' : 'Next scheduled runs'}
                            </div>
                            <ol
                                className="mt-1.5 max-h-40 list-decimal space-y-1 overflow-y-auto py-0 pl-4 text-[13px] text-gray-700 marker:text-gray-400"
                                aria-live="polite"
                            >
                                {preview.occurrences.map((occurrence) => (
                                    <li key={occurrence.utc}>
                                        {formatScheduleOccurrence(occurrence, timezone, {
                                            showWeekday: true,
                                            showTimeZone: false,
                                        })}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </>
            )}
        </section>
    )
}

const XIcon = ({ className = '' }: { className?: string }) => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className={className}
    >
        <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
)

const PlusIcon = ({ className = '' }: { className?: string }) => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        className={className}
    >
        <path d="M8 3v10M3 8h10" />
    </svg>
)
